<template>
  <div class="module-options">
    <template v-if="wordTrackingEnabled">
      <WordTrackingOptions
        :canvas-name="canvasName"
        :module-data="moduleData"
      />
    </template>
    <template v-else>
      <div class="module-options__inner">
        <div class="module-options__item module-options__item--theme">
          <fieldset
            class="module-options__theme-fieldset"
            role="radiogroup"
          >
            <legend class="module-options__legend">
              {{ $t('Theme') }}
            </legend>
            <div class="module-options__theme-variations">
              <label
                v-for="theme in filteredThemes"
                :key="theme.slug"
              >
                <input
                  type="radio"
                  class="module-options__theme-input"
                  :checked="selectedThemeSlug === theme.slug"
                  :name="`module-options__theme-input--${moduleData.id}`"
                  :value="theme.slug"
                  :aria-label="$t('Theme %(themeName)s %(themeVariation)s', {
                    themeName: theme.name,
                    themeVariation: theme.variation,
                  })"
                  @change="changeThemeVariation(theme.name, theme.variation)"
                >
                <div
                  class="module-options__theme-preview"
                  :class="`editor-theme__text--${theme.slug}`"
                  aria-hidden="true"
                />
              </label>
            </div>
          </fieldset>
        </div>
        <div
          class="module-options__item module-options__item--overflow"
        >
          <legend class="module-options__legend">
            {{ $t('Text Options') }}
          </legend>
          <NebulaSelect>
            <template #select>
              <select
                class="nebula-select__element"
                :value="moduleData.options.overflow"
                @change="changeOverflow"
              >
                <template v-if="draftType === 'board' && canvasName === 'modules'">
                  <NebulaSelectOption
                    :option="{
                      text: $t('Hidden'),
                      value: 'hidden',
                    }"
                  />
                  <NebulaSelectOption
                    :option="{
                      text: $t('Overflow'),
                      value: 'overflow',
                    }"
                  />
                </template>
                <template v-else>
                  <NebulaSelectOption
                    v-if="moduleData.options.overflow === 'hidden'"
                    :option="{
                      text: $t('Autosize (Default)'),
                      value: 'hidden',
                    }"
                  />
                  <NebulaSelectOption
                    v-if="['overflow', 'scroll'].includes(moduleData.options.overflow)"
                    :option="{
                      text: $t('Autosize (Default)'),
                      value: 'overflow',
                    }"
                  />
                </template>
                <NebulaSelectOption
                  :option="{
                    text: $t('Scroll'),
                    value: 'scroll',
                  }"
                />
              </select>
            </template>
          </NebulaSelect>
        </div>
      </div>
      <ShapeBorderOptions
        :canvas-name="canvasName"
        :module-data="moduleData"
      />
    </template>
  </div>
</template>

<script>
import { NebulaSelect, NebulaSelectOption } from '@discoveryedu/nebula-components';
import { cloneDeep, get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import ShapeBorderOptions from '@/components/modules/ShapeBorderOptions.vue';
import WordTrackingOptions from '@/components/WordTrackingOptions.vue';
import { themes as themeConfigs } from '@/lib/constants/themes.json';
import { UPDATE_MODULE } from '@/lib/constants/store';
import {
  useEditorStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'TextModuleOptions',
  components: {
    NebulaSelect,
    NebulaSelectOption,
    ShapeBorderOptions,
    WordTrackingOptions,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    canvasName: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'draftType',
    ]),
    ...mapState(useUserStore, [
      'isDeUser',
    ]),
    filteredThemes() {
      // Find the config for the current page theme. Fall back to the default theme
      const currentPageTheme = get(this, 'currentPage.options.theme', {});
      const currentPageThemeConfig = themeConfigs
        .find((theme) => theme.id === (currentPageTheme.name || 'default'));

      const themes = [
        {
          slug: 'default-1', // black text, white background
          name: 'default',
          variation: 1,
        },
        // Go through the current theme's text color variations and add
        // an option for each one, like light-1, dark-2, etc.
        ...currentPageThemeConfig.textColors.reduce((acc, curr) => {
          const name = curr.id;
          curr.variations.forEach((variation) => {
            acc.push({
              slug: `${name}-${variation.id}`,
              name,
              variation: variation.id,
            });
          });

          return acc;
        }, []),
        {
          slug: 'default-2', // white text, transparent background
          name: 'default',
          variation: 2,
          editorialOnly: true,
        },
        {
          slug: 'default-3', // black text, transparent background
          name: 'default',
          variation: 3,
          editorialOnly: true,
        },
        {
          slug: 'default-4', // gray text, transparent background
          name: 'default',
          variation: 4,
          editorialOnly: true,
        },
      ];

      return themes.filter((theme) => (this.isDeUser || !theme.editorialOnly));
    },
    selectedThemeSlug() {
      const theme = get(this, 'moduleData.options.theme', {});
      return `${theme.name}-${theme.variation}`;
    },
    wordTrackingEnabled() {
      return this.canvasName !== 'notes'
        && get(this, 'moduleData.options.word_tracking.enabled', false);
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      UPDATE_MODULE,
    ]),
    changeThemeVariation(name, variation) {
      const data = cloneDeep(this.moduleData);
      data.options.theme = {
        ...data.options.theme,
        name,
        variation,
      };
      this[UPDATE_MODULE]({ canvasName: this.canvasName, payload: data });
    },
    changeOverflow(event) {
      const data = cloneDeep(this.moduleData);
      data.options.overflow = event.target.value;
      this[UPDATE_MODULE]({ canvasName: this.canvasName, payload: data });
    },
  },
};
</script>
